import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { SectionShell } from "@/pages/SectionShell";
import { Button } from "@/components/Button";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useI18n, useT } from "@/contexts/I18nContext";
import { useToast } from "@/contexts/ToastContext";
import { groupsByUid, useGroups, usePeople } from "@/lib/hooks/usePeople";
import { call, CALLABLES } from "@/services/callables";
import { formatDateTime } from "@/lib/format";

/**
 * One person.
 *
 * **No performance history.** A student's scores and a teacher's notes belong
 * to the lesson that produced them and are reached through it — that is not a
 * navigation decision but the product's central promise, and a "recent scores"
 * panel here would quietly undo it.
 */
export function PersonPage() {
  const { uid = "" } = useParams();
  const t = useT();
  const { locale } = useI18n();
  const navigate = useNavigate();
  const { success, showError, info } = useToast();

  const { people, loading } = usePeople();
  const { groups } = useGroups();
  const person = people.find((candidate) => candidate.uid === uid);

  const theirGroups = useMemo(() => groupsByUid(groups).get(uid) ?? [], [groups, uid]);
  const [deleting, setDeleting] = useState(false);
  const [busy, setBusy] = useState(false);

  if (loading) {
    return (
      <SectionShell titleKey="people.title">
        <p className="text-sm text-ink-muted">{t("common.loading")}</p>
      </SectionShell>
    );
  }

  if (!person) {
    return (
      <SectionShell titleKey="people.title">
        <p className="text-ink-muted">{t("error.user.notFound")}</p>
      </SectionShell>
    );
  }

  const act = async (action: () => Promise<void>) => {
    setBusy(true);
    try {
      await action();
    } catch (error) {
      showError(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <SectionShell titleKey="person.title" titleParams={{ name: person.displayName }}>
      <div className="grid max-w-4xl gap-10 md:grid-cols-2">
        <section>
          <h2 className="section-title">{t("person.section.identity")}</h2>
          <dl className="mt-3 grid grid-cols-[10rem_1fr] gap-y-2 text-sm">
            <dt className="text-ink-muted">{t("person.displayName")}</dt>
            <dd className="text-ink">{person.displayName}</dd>

            <dt className="text-ink-muted">{t("person.username")}</dt>
            <dd className="text-ink">{person.username ?? t("person.noUsername")}</dd>

            <dt className="text-ink-muted">{t("person.email")}</dt>
            <dd className="text-ink">{person.email ?? t("person.emailNone")}</dd>

            <dt className="text-ink-muted">{t("person.role")}</dt>
            <dd className="text-ink">{t(`role.${person.role}`)}</dd>

            <dt className="text-ink-muted">{t("person.created")}</dt>
            <dd className="text-ink">{formatDateTime(locale, person.createdAt)}</dd>

            <dt className="text-ink-muted">{t("person.lastActive")}</dt>
            <dd className="text-ink">
              {person.lastActiveAt
                ? formatDateTime(locale, person.lastActiveAt)
                : t("people.never")}
            </dd>
          </dl>

          <div className="mt-6 flex flex-wrap gap-2">
            <Button
              variant="secondary"
              busy={busy}
              onClick={() =>
                act(async () => {
                  const result = await call<
                    { uid: string },
                    { temporaryPassword: string }
                  >(CALLABLES.resetUserPassword, { uid: person.uid });
                  // Shown once. There is nowhere to look it up afterwards, and
                  // an owner who closes this without writing it down must issue
                  // another — which is the honest cost of not storing it.
                  info("person.passwordIssued", {
                    name: person.displayName,
                    password: result.temporaryPassword,
                  });
                  info("person.passwordIssuedWarning");
                })
              }
            >
              {t("person.resetPassword")}
            </Button>

            <Button
              variant="secondary"
              busy={busy}
              onClick={() =>
                act(async () => {
                  await call(CALLABLES.setUserDisabled, {
                    uid: person.uid,
                    disabled: !person.disabled,
                    reason: "back office",
                  });
                  success("common.saved");
                })
              }
            >
              {person.disabled ? t("person.enable") : t("person.disable")}
            </Button>
          </div>
        </section>

        <section>
          <h2 className="section-title">{t("person.section.groups")}</h2>
          {theirGroups.length === 0 ? (
            <p className="mt-3 text-sm text-ink-muted">{t("person.noGroups")}</p>
          ) : (
            <ul className="mt-3 flex flex-col gap-1.5 text-sm">
              {theirGroups.map((group) => (
                <li key={group.id} className="text-ink">
                  {group.name}
                </li>
              ))}
            </ul>
          )}

        </section>
      </div>

      <section className="mt-12 max-w-4xl border-t border-line pt-8">
        <h2 className="section-title">{t("person.section.data")}</h2>
        <p className="mt-2 max-w-2xl text-sm text-ink-muted">{t("person.noHistory")}</p>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            variant="secondary"
            busy={busy}
            onClick={() =>
              act(async () => {
                /*
                  The callable returns the data, not a link to it. Turning it
                  into a file here means nothing is written to storage and there
                  is no URL to leak — a subject access request should not leave
                  a copy of somebody's data sitting behind a signed link.
                */
                const data = await call<{ uid: string }, Record<string, unknown>>(
                  CALLABLES.exportUser,
                  { uid: person.uid }
                );
                const blob = new Blob([JSON.stringify(data, null, 2)], {
                  type: "application/json",
                });
                const url = URL.createObjectURL(blob);
                const anchor = document.createElement("a");
                anchor.href = url;
                anchor.download = `${person.username ?? person.uid}.json`;
                anchor.click();
                URL.revokeObjectURL(url);
              })
            }
          >
            {t("person.export")}
          </Button>

          <Button variant="danger" onClick={() => setDeleting(true)}>
            {t("person.delete")}
          </Button>
        </div>
      </section>

      {/*
        Typing the person's name, not the word "delete". It is the one string on
        the screen that belongs to this person and to nobody else, so typing it
        cannot be done on the wrong page by accident.
      */}
      <ConfirmDialog
        open={deleting}
        onClose={() => setDeleting(false)}
        onConfirm={async (reason) => {
          await call(CALLABLES.deleteUser, { uid: person.uid, reason });
          success("common.saved");
          navigate("/people", { replace: true });
        }}
        titleKey="person.deleteTitle"
        titleParams={{ name: person.displayName }}
        bodyKey="person.deleteBody"
        expected={person.displayName}
        confirmKey="person.deleteConfirm"
        mismatchKey="person.deleteMismatch"
        withReason
      />
    </SectionShell>
  );
}
